import React from "react";
import { motion } from "framer-motion";
import { SectionHeader, Reveal, Pill } from "@/components/Bits";
import { PROFILE, SKILL_GROUPS, EDUCATION } from "@/data/portfolio";
import { GraduationCap, Heart, Lightbulb, Target } from "lucide-react";

const VALUES = [
  { icon: Lightbulb, title: "Curious by default", text: "Ask, validate, ship — then iterate fast on what users actually do.", accent: "peach" },
  { icon: Target, title: "Outcomes over output", text: "Bias for measurable wins: retention, revenue, activation.", accent: "lavender" },
  { icon: Heart, title: "Builder energy", text: "I love startup chaos and wearing many hats to unblock the team.", accent: "sage" },
];

export default function About() {
  return (
    <div data-testid="about-page" className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24">
      <SectionHeader
        eyebrow="About me"
        title="Product thinking with a"
        emphasis="playful pulse."
        sub="A quick intro to who I am, what I value, and how I work with teams to ship products people actually love."
      />

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7">
          <Reveal>
            <div className="soft-card p-8 md:p-10 relative overflow-hidden" data-testid="about-bio-card">
              <div className="blob bg-[var(--peach)] w-60 h-60 -top-20 -right-20" />
              <div className="relative">
                <Pill variant="peach">⚡ APM</Pill>
                <h3 className="mt-5 font-display text-3xl md:text-4xl font-medium">Hey, I&apos;m {PROFILE.name}.</h3>
                <p className="mt-5 text-base md:text-lg leading-relaxed text-[var(--text-muted)]">{PROFILE.bio}</p>
                <p className="mt-4 text-base md:text-lg leading-relaxed text-[var(--text-muted)]">
                  I&apos;ve owned products across <span className="text-[var(--text)] font-semibold">E-Commerce</span>,{" "}
                  <span className="text-[var(--text)] font-semibold">PaaS</span> and{" "}
                  <span className="text-[var(--text)] font-semibold">gamification</span> — moving the needle on
                  onboarding, retention, and revenue with tightly-scoped MVPs and obsessive customer research.
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-5 flex flex-col gap-6">
          <Reveal delay={0.05}>
            <div className="soft-card p-6" data-testid="about-quickfacts">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-semibold">Quick facts</p>
              <ul className="mt-4 space-y-2.5 font-medium">
                <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[var(--peach)]" /> Based in Bengaluru · open to remote</li>
                <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[var(--lavender)]" /> 2 companies · 1.5+ years PM</li>
                <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[var(--sage)]" /> MBA Finance · IIT Patna Agentic AI</li>
                <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[var(--peach)]" /> Loves growth + gamification loops</li>
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="soft-card p-6 bg-[var(--lavender-soft)]" data-testid="about-currently">
              <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--text-muted)]">Currently</p>
              <p className="mt-3 font-display text-2xl font-medium leading-snug">
                Exploring PM opportunities in growth, fintech &amp; consumer apps.
              </p>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Values */}
      <div className="mt-24" data-testid="about-values">
        <SectionHeader eyebrow="How I work" title="A few things I" emphasis="believe in." />
        <div className="grid md:grid-cols-3 gap-6">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.06}>
              <motion.div whileHover={{ y: -4 }} className="soft-card p-7 h-full">
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center"
                  style={{ background: `var(--${v.accent}-soft)`, color: `var(--${v.accent === "peach" ? "peach" : v.accent})` }}
                >
                  <v.icon className="w-5 h-5 text-[var(--text)]" />
                </div>
                <h4 className="mt-5 font-display text-xl font-medium">{v.title}</h4>
                <p className="mt-2 text-[var(--text-muted)]">{v.text}</p>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Skills */}
      <div className="mt-24" data-testid="about-skills">
        <SectionHeader eyebrow="Skills" title="My" emphasis="playbook." sub="Tools, domains and superpowers I bring to every product I touch." />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SKILL_GROUPS.map((g, i) => (
            <Reveal key={g.title} delay={i * 0.05}>
              <div className="soft-card p-6 h-full">
                <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-semibold">Stack</p>
                <h4 className="mt-2 font-display text-xl font-medium">{g.title}</h4>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {g.items.map((it) => (
                    <Pill key={it} variant={g.variant}>{it}</Pill>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Education */}
      <div className="mt-24" data-testid="about-education">
        <SectionHeader eyebrow="Education" title="Where I learned the" emphasis="craft." />
        <div className="grid md:grid-cols-3 gap-6">
          {EDUCATION.map((e, i) => (
            <Reveal key={e.title} delay={i * 0.06}>
              <div className="soft-card p-7 h-full">
                <div className="flex items-center gap-3">
                  <div
                    className="w-11 h-11 rounded-2xl flex items-center justify-center"
                    style={{ background: `var(--${e.accent}-soft)` }}
                  >
                    <GraduationCap className="w-5 h-5" />
                  </div>
                  <Pill variant={e.accent}>{e.badge}</Pill>
                </div>
                <h4 className="mt-5 font-display text-xl font-medium leading-snug">{e.title}</h4>
                <p className="mt-1.5 text-[var(--text-muted)]">{e.sub}</p>
                <p className="mt-3 text-xs uppercase tracking-[0.18em] font-semibold text-[var(--text-muted)]">{e.period}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}
